import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Select, SelectItem,
} from '@heroui/react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAreaMaterials, useCreateAreaMaterial, useUpdateAreaMaterial, useDeleteAreaMaterial } from '@/api/area-materials';
import { useProjectMaterials } from '@/api/project-materials';
import { useMaterialsLibrary } from '@/api/materials-library';

const SPEC_CODES = [
  { code: 'box_panel_spec', label: 'Box Panel' },
  { code: 'back_panel_spec', label: 'Back Panel' },
  { code: 'stretcher_spec', label: 'Stretcher' },
  { code: 'shelf_spec', label: 'Shelf' },
  { code: 'door_spec', label: 'Door' },
  { code: 'drawer_box_spec', label: 'Drawer Box' },
  { code: 'edgeband_spec', label: 'Edgeband' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  projectId: string;
}

export default function AreaMaterialsModal({ isOpen, onClose, areaId, projectId }: Props) {
  const { data: areaAssignments = [] } = useAreaMaterials(areaId);
  const { data: projectAssignments = [] } = useProjectMaterials(projectId);
  const { data: materials = [] } = useMaterialsLibrary();
  const createMutation = useCreateAreaMaterial();
  const updateMutation = useUpdateAreaMaterial();
  const deleteMutation = useDeleteAreaMaterial();

  const areaMap = new Map((areaAssignments as any[]).map((a) => [a.spec_code, a]));
  const projectMap = new Map((projectAssignments as any[]).map((a) => [a.spec_code, a]));

  const getMaterialName = (id: string | undefined) => {
    if (!id) return null;
    const mat = (materials as any[]).find((m) => m.id === id);
    return mat?.name ?? null;
  };

  const handleAssign = async (specCode: string, materialId: string) => {
    const existing = areaMap.get(specCode);
    try {
      if (existing) {
        await updateMutation.mutateAsync({ id: existing.id, material_id: materialId });
      } else {
        await createMutation.mutateAsync({
          area_id: areaId,
          spec_code: specCode,
          material_id: materialId,
        });
      }
      toast.success('Area material set');
    } catch {
      toast.error('Failed to set material');
    }
  };

  const handleRemove = async (specCode: string) => {
    const existing = areaMap.get(specCode);
    if (!existing) return;
    try {
      await deleteMutation.mutateAsync(existing.id);
      toast.success('Area override removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-surface)] text-[var(--text-primary)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader>Area Material Overrides</ModalHeader>
        <ModalBody>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            Override project-level material defaults for this area. Leave blank to inherit the project default.
          </p>
          <div className="space-y-2">
            {SPEC_CODES.map(({ code, label }) => {
              const areaAssignment = areaMap.get(code);
              const projectAssignment = projectMap.get(code);
              const projectMaterialName = getMaterialName(projectAssignment?.material_id);

              return (
                <div key={code} className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: 'var(--bg-panel)' }}>
                  <div className="w-28 flex-shrink-0">
                    <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{label}</div>
                    {projectMaterialName && (
                      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        Project: {projectMaterialName}
                      </div>
                    )}
                  </div>
                  <Select
                    aria-label={`Material for ${label}`}
                    selectedKeys={areaAssignment?.material_id ? [areaAssignment.material_id] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0] as string;
                      if (val) handleAssign(code, val);
                    }}
                    size="sm"
                    className="flex-1"
                    placeholder={projectMaterialName ? `Inherit: ${projectMaterialName}` : 'Not set'}
                    classNames={{ trigger: 'bg-[var(--bg-surface)] border-[var(--border)]' }}
                  >
                    {(materials as any[]).map((m) => (
                      <SelectItem key={m.id}>{m.name}</SelectItem>
                    ))}
                  </Select>
                  {areaAssignment && (
                    <button onClick={() => handleRemove(code)} className="p-1 rounded hover:bg-[var(--bg-hover)]" title="Remove override">
                      <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="bordered" onPress={onClose} className="border-[var(--border)] text-[var(--text-secondary)]">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
